import { Routes, Route, BrowserRouter, HashRouter } from "react-router-dom";
import ExistingMember from "./pages/ExistingMember";
import Error from "./pages/Error";
import CreateProfile from "./pages/Single/CreateProfile";
import PersonalInfo from "./pages/Single/PersonalInfo";
import PhoneVerification from "./pages/Single/PhoneVerification";
import VerificationCode from "./pages/Single/VerificationCode";
import Address from "./pages/Single/Address";
import { ApplicationLayout } from "./components/shared/ApplicationLayout";
import { PreApplicationLayout } from "./components/shared/PreApplicationLayout";
import LegalTerms from "./pages/Single/LegalTerms";
import Sin from "./pages/Single/Sin";
import ReviewApplication from "./pages/Single/ReviewApplication";
import ConfirmApplication from "./pages/Single/ConfirmApplication";

import JointCreateProfile from "./pages/Joint/CreateProfile";
import JointPersonalInfo from "./pages/Joint/PersonalInfo";
import JointPhoneVerification from "./pages/Joint/PhoneVerification";
import JointVerificationCode from "./pages/Joint/VerificationCode";
import JointAddress from "./pages/Joint/Address";
import JointLegalTerms from "./pages/Joint/LegalTerms";
import JointReviewApplication from "./pages/Joint/ReviewApplication";
import JointSin from "./pages/Joint/Sin";
import VerifyIdentification from "./pages/Single/VerifyIdentification";
import ApplicationDecline from "./pages/ApplicationDecline";
import JointConfirmApplication from "./pages/Joint/ConfirmApplication";
import NotFound from "./components/Error/NotFound";
import SelectApplicationType from "./pages/Single/SelectApplicationType";
import SelectAssessmentType from "./pages/Single/SelectAssessmentType";
import MortgageTerm from "./pages/Single/MortgageTerm";
import Amortization from "./pages/Single/Amortization";
import PurchasePrice from "./pages/Single/PurchasePrice";
import DownPayment from "./pages/Single/DownPayment";
import AcceptTerms from "./pages/Single/AcceptTerms";
import Login from "./pages/Login";
import AnswerSecurity from "./pages/AnswerSecurity";
import Logout from "./pages/Logout";
import TwoFactorAuthentication from "./pages/TwoFactorAuthentication";
import ForgotPassword from "./pages/ForgotPassword";
import NewPassword from "./pages/NewPassword";
import CoApplicant from "./pages/Single/CoApplicant";
import PropertyDetails from "./pages/Single/PropertyDetails";
import PropertyPurpose from "./pages/Single/PropertyPurpose";
import PreviousAddress from "./pages/Single/PreviousAddress";
import PropertyValue from "./pages/Single/PropertyValue";
import MortgageBalance from "./pages/Single/MortgageBalance";
import AddCoApplicant from "./pages/Single/AddCoApplicant";
import ClosingDate from "./pages/Single/ClosingDate";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import FinancingCondition from "./pages/Single/FinancingCondition";
import FundingDate from "./pages/Single/FundingDate";
import ProtectedRoute from "./components/shared/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import AcceptIDVerificationTerms from "./pages/VerifyID/AcceptIDVerificationTerms";
import Dashboard from "./pages/Dashboard";
import JointMortgageDetails from "./pages/Joint/MortgageDetails";
import JointPreviousAddress from "./pages/Joint/PreviousAddress";
import JointPropertyDetails from "./pages/Joint/PropertyDetails";
import ConfirmBridgeLoan from "./pages/Single/ConfirmBridgeLoan";
import IDCheckFailed from "./pages/IDCheckFailed";
import ConfirmIDSubmission from "./pages/VerifyID/ConfirmIDSubmission";
import { AnimatePresence } from "framer-motion";
import PasswordResetRequested from "./pages/PasswordResetRequested";
import Attestation from "./pages/Single/Attestation";
import ConsultationRoute from "./components/shared/ConsultationRoute";
import Employment from "./pages/Single/Employment";
import PreviousEmployment from "./pages/Single/PreviousEmployment";
import Assets from "./pages/Single/Assets";
import JointEmployment from "./pages/Joint/Employment";
import JointPreviousEmployment from "./pages/Joint/PreviousEmployment";
import ReFiPropertyDetails from "./pages/Single/ReFiPropertyDetails";
import SSOLoginDocIQ from "./pages/SSOLoginDocIQ";
import LoanAmount from "./pages/Single/LoanAmount";
import SSOLoginROB from "./pages/SSOLoginROB";

function App() {
  const basePath = import.meta.env.VITE_PUBLIC_BASE_PATH; //import.meta.env.VITE_PUBLIC_BASE_PATH;

  const baseURL = document.querySelector('base')?.href + '#/?';

  return (
    <AuthProvider>
      <hr className="md:-mr-18" />
      <AnimatePresence>
        <Routes>
          <Route element={<PreApplicationLayout />}>
            <Route path="/" element={<ExistingMember />} />
            <Route path="/error" element={<Error />} />
            <Route path="/error/:errId" element={<Error />} />
            <Route path="/single/accept-terms" element={<AcceptTerms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ssoLogin" element={<SSOLoginDocIQ />} />
            <Route path="/sso" element={<SSOLoginROB />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/security" element={<AnswerSecurity />} />
              <Route path="/two-fa-security" element={<TwoFactorAuthentication />} />
            </Route>
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-reset-requested" element={<PasswordResetRequested />} />
            <Route path="/new-password" element={<NewPassword />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="*" element={<NotFound />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/confirm-consent" element={<Attestation />} />
              <Route path="/single/confirm-application" element={<ConfirmApplication />} />
              <Route path="/single/confirm-bridge-loan" element={<ConfirmBridgeLoan />} />
              <Route path="/single/application-error" element={<IDCheckFailed />} />
              <Route path="/verify-id/upload-complete" element={<ConfirmIDSubmission />} />
              <Route path="/joint/confirm-application" element={<JointConfirmApplication />} />
            </Route>
          </Route>
          <Route element={<ApplicationLayout sidebar={0} hideMobileMenu={true} />}>
            <Route element={<ProtectedRoute />}>
              <Route path="/home" element={<Dashboard />} />
            </Route>
          </Route>
          <Route element={<PreApplicationLayout />}>
            <Route path="/joint/accept-terms" element={<AcceptTerms />} />
          </Route>
          <Route element={<ApplicationLayout sidebar={1} isJoint={false} />}>
            <Route path="/single">
              <Route path="/single/create-profile" element={<CreateProfile />} />
              <Route path="/single/personal-info" element={<PersonalInfo />} />
              <Route path="/single/phone-verification" element={<PhoneVerification />} />
              <Route path="/single/verification-code" element={<VerificationCode />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<ConsultationRoute />}>
                  <Route path="/single/select-application" element={<SelectApplicationType />} />
                  <Route path="/single/select-assessment" element={<SelectAssessmentType />} />
                  <Route path="/single/mortgage-term" element={<MortgageTerm />} />
                  <Route path="/single/amortization" element={<Amortization />} />
                  <Route path="/single/purchase-price" element={<PurchasePrice />} />
                  <Route path="/single/property-value" element={<PropertyValue />} />
                  <Route path="/single/mortgage-balance" element={<MortgageBalance />} />
                  <Route path="/single/loan-amount" element={<LoanAmount />} />
                  <Route path="/single/down-payment" element={< DownPayment />} />
                </Route>

                <Route path="/single/sin" element={<Sin />} />
                <Route path="/single/address" element={<Address />} />
                <Route path="/single/previousaddress" element={<PreviousAddress />} />
                <Route path="/single/employment" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Employment />
                  </LocalizationProvider>
                } />
                <Route path="/single/previous-employment" element={<PreviousEmployment />} />
                <Route path="/single/assets" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Assets />
                  </LocalizationProvider>
                } />
                <Route path="/single/co-applicant" element={<CoApplicant />} />
                <Route path="/single/add-co-applicant" element={<AddCoApplicant />} />
                <Route path="/single/property-details" element={<PropertyDetails />} />
                <Route path="/single/refi-property-details" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ReFiPropertyDetails />
                  </LocalizationProvider>
                } />
                <Route path="/single/property-purpose" element={<PropertyPurpose />} />
                <Route path="/single/closing-date" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ClosingDate />
                  </LocalizationProvider>
                } />
                <Route path="/single/financing-condition" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FinancingCondition />
                  </LocalizationProvider>
                } />
                <Route path="/single/funding-date" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FundingDate />
                  </LocalizationProvider>
                } />
                <Route path="/single/review-application" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ReviewApplication />
                  </LocalizationProvider>
                } />
                <Route path="/single/legal-terms" element={<LegalTerms />} />
                <Route path="/single/application-decline" element={<ApplicationDecline />} />
              </Route>
            </Route>
          </Route>
          <Route element={<ApplicationLayout sidebar={1} isJoint={true} />}>
            <Route path="/joint">
              <Route path="/joint/create-profile" element={<JointCreateProfile />} />
              <Route path="/joint/phone-verification" element={<JointPhoneVerification />} />
              <Route path="/joint/verification-code" element={<JointVerificationCode />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/joint/mortgage-details" element={<JointMortgageDetails />} />
                <Route path="/joint/personal-info" element={<JointPersonalInfo />} />
                <Route path="/joint/sin" element={<JointSin />} />
                <Route path="/joint/address" element={<JointAddress />} />
                <Route path="/joint/previous-address" element={<JointPreviousAddress />} />
                <Route path="/joint/employment" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <JointEmployment />
                  </LocalizationProvider>
                } />
                <Route path="/joint/previous-employment" element={<JointPreviousEmployment />} />
                <Route path="/joint/property-details" element={<JointPropertyDetails />} />
                <Route path="/joint/legal-terms" element={<JointLegalTerms />} />
                <Route path="/joint/review-application" element={
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <JointReviewApplication />
                  </LocalizationProvider>
                } />
              </Route>
            </Route>
          </Route>
          <Route element={<ApplicationLayout sidebar={1} />}>
            <Route path="/verify-id">
              <Route element={<ProtectedRoute />}>
                <Route path="/verify-id/accept-terms" element={<AcceptIDVerificationTerms />} />
                <Route path="/verify-id/verify-identification" element={<VerifyIdentification />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </AnimatePresence>
    </AuthProvider>
  );
}

export default App;
